import { useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { TokenCostPanel } from "@/components/tokens/TokenCostPanel";
import { ToolCostSection } from "@/components/tokens/ToolCostSection";
import { TokenEconomyPanel } from "@/components/tokens/TokenEconomyPanel";
import { PeriodFilter, sinceDate } from "@/components/tokens/PeriodFilter";
import { BurnRatePanel } from "@/components/cost/BurnRatePanel";
import { EpicBreakdownSection } from "@/components/cost/EpicBreakdownSection";
import { DeveloperBreakdownSection } from "@/components/cost/DeveloperBreakdownSection";
import { CostPerStoryPointSection } from "@/components/cost/CostPerStoryPointSection";
import { useProjects } from "@/hooks/useProjects";
import { useStoryCostEvents } from "@/hooks/useStoryCostEvents";
import { useCalibrationEntries } from "@/hooks/useCalibrationEntries";
import { ChevronRight } from "lucide-react";

export function CostPage() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [periodDays, setPeriodDays] = useState(30);
  const { projects } = useProjects();

  const toolCostFilters = useMemo(() => {
    const since = sinceDate(periodDays);
    return since ? { since } : {};
  }, [periodDays]);

  // Hoist once — Views D and E share the same fetch; client-side date filter (D1)
  const storyCostSince = sinceDate(periodDays) ?? undefined;
  const storyCostEvents = useStoryCostEvents(storyCostSince);

  // View A ignores the page PeriodFilter — trend semantics require full
  // calendar months. Same react-query key (no `since`), so this is a cache
  // hit on the fetch above, not an extra network call (RAISE-16869 D2).
  const storyCostEventsUnfiltered = useStoryCostEvents();
  const calibration = useCalibrationEntries();

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Cost & Consumption
          </h1>
          <p className="text-sm text-muted-foreground">
            Organization-wide cost aggregation across all tools and projects.
          </p>
        </div>
      </div>

      {/* Page-level Period Filter (shared across all panels, SD2/AC6) */}
      <PeriodFilter value={periodDays} onChange={setPeriodDays} />

      {/* Section 1: Token cost (org mode) */}
      <TokenCostPanel projectSlug={null} externalPeriodDays={periodDays} />

      {/* Section 2: Tool cost (org mode) */}
      <ToolCostSection projectSlug={null} filters={toolCostFilters} />

      {/* Section 3: Cost efficiency trend (org mode) */}
      <TokenEconomyPanel
        projectSlug={null}
        externalPeriodDays={periodDays}
      />

      {/* Section 4: Cost per Story Point — View A, experimental (RAISE-16869) */}
      <CostPerStoryPointSection
        events={storyCostEventsUnfiltered.events}
        eventsLoading={storyCostEventsUnfiltered.loading}
        eventsError={storyCostEventsUnfiltered.error}
        planGated={storyCostEventsUnfiltered.planGated}
        calibration={calibration.entries}
        calibrationLoading={calibration.loading}
        calibrationError={calibration.error}
        failedProjects={calibration.failedProjects}
      />

      {/* Section 5: Burn Rate — View C (RAISE-16867) */}
      <BurnRatePanel periodDays={periodDays} />

      {/* Section 6: Story Cost Breakdown — View D (RAISE-16867) */}
      <EpicBreakdownSection
        events={storyCostEvents.events}
        loading={storyCostEvents.loading}
        error={storyCostEvents.error}
        planGated={storyCostEvents.planGated}
        truncated={storyCostEvents.truncated}
        total={storyCostEvents.total}
      />

      {/* Section 7: Developer Cost Breakdown — View E (RAISE-16867) */}
      <DeveloperBreakdownSection
        events={storyCostEvents.events}
        loading={storyCostEvents.loading}
        error={storyCostEvents.error}
        planGated={storyCostEvents.planGated}
        truncated={storyCostEvents.truncated}
        total={storyCostEvents.total}
      />

      {/* Section 8: Project drill-down links (AC7) */}
      {projects.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-base font-semibold">Project Drill-Down</h2>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <Link
                key={p.id}
                to={`/${orgSlug}/projects/${p.slug}/metrics`}
                className="flex items-center justify-between rounded-lg border px-4 py-3 text-sm hover:bg-accent transition-colors"
              >
                <span className="font-medium">{p.name}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
